import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import SubscriptionService from './SubscriptionService';
import SubscriptionNotification from './SubscriptionNotification';
import './GlobalSubscriptionNotification.css';

const GlobalSubscriptionNotification = () => {
  const { user, token } = useAuth();
  const [warnings, setWarnings] = useState([]);
  const [isVisible, setIsVisible] = useState(false);

  const checkSubscriptionWarnings = useCallback(async () => {
    try {
      // Add null check for user
      if (!user || !user._id || !token) {
        return;
      }

      // Check if notification was already dismissed in this session
      const dismissedKey = `subscription-notification-dismissed-${user._id}`;
      const isDismissed = sessionStorage.getItem(dismissedKey);

      if (isDismissed === 'true') {
        console.log('[GlobalSubscriptionNotification] Notification already dismissed in this session');
        setIsVisible(false);
        return;
      }

      const warnings = await SubscriptionService.checkWarnings(user._id, token);
      console.log('[GlobalSubscriptionNotification] Warnings received:', warnings);
      console.log('[GlobalSubscriptionNotification] Number of warnings:', warnings.length);
      setWarnings(warnings);
      setIsVisible(warnings.length > 0);
    } catch (error) {
      console.error('Error checking subscription warnings:', error);
    }
  }, [user, token]);

  useEffect(() => {
    if (user && user._id && token) {
      checkSubscriptionWarnings();
    } else {
      // Reset warnings when user is not available
      setWarnings([]);
      setIsVisible(false);
    }
  }, [user, token, checkSubscriptionWarnings]);

  // Early return if user is not available
  if (!user || !user._id || !token) {
    return null;
  }

  const handleDismissWarning = (index) => {
    if (index !== undefined) {
      // Dismiss specific warning
      setWarnings(prev => prev.filter((_, i) => i !== index));
      if (warnings.length <= 1) {
        setIsVisible(false);
        // Store dismissal in sessionStorage
        if (user && user._id) {
          const dismissedKey = `subscription-notification-dismissed-${user._id}`;
          sessionStorage.setItem(dismissedKey, 'true');
        }
      }
    } else {
      // Dismiss all warnings (when close button is clicked)
      setWarnings([]);
      setIsVisible(false);
      // Store dismissal in sessionStorage
      if (user && user._id) {
        const dismissedKey = `subscription-notification-dismissed-${user._id}`;
        sessionStorage.setItem(dismissedKey, 'true');
        console.log('[GlobalSubscriptionNotification] Notification dismissed for this session');
      }
    }
  };

  const handleUpgradeAction = () => {
    if (user && user._id) {
      // Navigate to membership page for upgrade
      window.location.href = '/membership';
    }
  };

  const renderWarning = (warning, index) => {
    switch (warning.type) {
      case 'renewal':
        return (
          <div key={index} className="warning-item renewal-warning">
            <i className="fas fa-clock"></i>
            <span>{warning.message}</span>
          </div>
        );
      case 'expired':
        return (
          <div key={index} className="warning-item expired-warning">
            <i className="fas fa-exclamation-triangle"></i>
            <span>{warning.message}</span>
            <button onClick={handleUpgradeAction} className="btn btn-sm btn-primary">
              Upgrade Now
            </button>
          </div>
        );
      default:
        return (
          <div key={index} className="warning-item">
            <i className="fas fa-info-circle"></i>
            <span>{warning.message}</span>
          </div>
        );
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="global-subscription-notification">
      <div className="notification-container">
        <div className="subscription-warnings">
          <div className="warnings-header">
            <i className="fas fa-bell"></i>
            <span>Subscription Notifications</span>
            <button onClick={() => handleDismissWarning()} className="close-btn">
              <i className="fas fa-times"></i>
            </button>
          </div>
          <div className="warnings-content">
            {warnings.map((warning, index) => renderWarning(warning, index))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalSubscriptionNotification;
